import { useState, useCallback, useEffect } from "react";
import { compose } from "lodash/fp";
import { startOfMonth, startOfDay, endOfDay, startOfWeek, addDays, max, min, isBefore, isAfter, addMonths, subMonths, addYears, subYears, subDays, addWeeks, subWeeks, endOfWeek } from "date-fns/fp";
function useDatePicker(config = {}) {
  const [monthInView, setMonthInView] = useState(
    startOfMonth(config.initialValue || /* @__PURE__ */ new Date())
  );
  const [highlightedDate, setHighlightedDate] = useState();
  const minDate = config.minDate ? startOfDay(config.minDate) : void 0;
  const maxDate = config.maxDate ? endOfDay(config.maxDate) : void 0;
  const firstDateInView = startOfWeek(monthInView);
  const lastDateInView = addDays(41, firstDateInView);
  const clampLowerMonth = useCallback(
    (date) => minDate ? max([startOfMonth(minDate), date]) : date,
    [minDate]
  );
  const clampUpperMonth = useCallback(
    (date) => maxDate ? min([startOfMonth(maxDate), date]) : date,
    [maxDate]
  );
  const handleHighlightedDateChange = useCallback(
    (changeFunc, clamp = false) => setHighlightedDate((date) => {
      if (!date) {
        return startOfWeek(startOfMonth(monthInView || /* @__PURE__ */ new Date()));
      }
      const newDate = changeFunc(date);
      if (isBefore(minDate, newDate)) {
        return clamp ? minDate : date;
      }
      if (isAfter(maxDate, newDate)) {
        return clamp ? maxDate : date;
      }
      return newDate;
    }),
    [maxDate, minDate, monthInView]
  );
  const viewNextMonth = useCallback(() => {
    setHighlightedDate(void 0);
    setMonthInView(compose(clampUpperMonth, addMonths(1)));
  }, [clampUpperMonth]);
  const viewPreviousMonth = useCallback(() => {
    setHighlightedDate(void 0);
    setMonthInView(compose(clampLowerMonth, subMonths(1)));
  }, [clampLowerMonth]);
  const viewNextYear = useCallback(() => {
    setHighlightedDate(void 0);
    setMonthInView(compose(clampUpperMonth, addYears(1)));
  }, [clampUpperMonth]);
  const viewPreviousYear = useCallback(() => {
    setHighlightedDate(void 0);
    setMonthInView(compose(clampLowerMonth, subYears(1)));
  }, [clampLowerMonth]);
  const highlightNextDay = useCallback(() => {
    handleHighlightedDateChange(addDays(1));
  }, [handleHighlightedDateChange]);
  const highlightPreviousDay = useCallback(() => {
    handleHighlightedDateChange(subDays(1));
  }, [handleHighlightedDateChange]);
  const highlightNextWeek = useCallback(() => {
    handleHighlightedDateChange(addWeeks(1));
  }, [handleHighlightedDateChange]);
  const highlightPreviousWeek = useCallback(() => {
    handleHighlightedDateChange(subWeeks(1));
  }, [handleHighlightedDateChange]);
  const highlightStartOfWeek = useCallback(() => {
    handleHighlightedDateChange(startOfWeek, true);
  }, [handleHighlightedDateChange]);
  const highlightEndOfWeek = useCallback(() => {
    handleHighlightedDateChange(endOfWeek, true);
  }, [handleHighlightedDateChange]);
  const highlightNextMonth = useCallback(() => {
    handleHighlightedDateChange(addMonths(1), true);
  }, [handleHighlightedDateChange]);
  const highlightPreviousMonth = useCallback(() => {
    handleHighlightedDateChange(subMonths(1), true);
  }, [handleHighlightedDateChange]);
  const highlightNextYear = useCallback(() => {
    handleHighlightedDateChange(addYears(1), true);
  }, [handleHighlightedDateChange]);
  const highlightPreviousYear = useCallback(() => {
    handleHighlightedDateChange(subYears(1), true);
  }, [handleHighlightedDateChange]);
  useEffect(() => {
    if (highlightedDate && (isBefore(firstDateInView, highlightedDate) || isAfter(lastDateInView, highlightedDate))) {
      setMonthInView(startOfMonth(highlightedDate));
    }
  }, [firstDateInView, highlightedDate, lastDateInView]);
  return {
    highlightEndOfWeek,
    highlightNextDay,
    highlightNextMonth,
    highlightNextWeek,
    highlightNextYear,
    highlightPreviousDay,
    highlightPreviousMonth,
    highlightPreviousWeek,
    highlightPreviousYear,
    highlightStartOfWeek,
    highlightedDate,
    monthInView,
    setHighlightedDate,
    viewNextMonth,
    viewNextYear,
    viewPreviousMonth,
    viewPreviousYear
  };
}
export {
  useDatePicker
};
